import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import QuoteForm from "@/components/QuoteForm";

const residentialServices = [
  { name: "Custom Shower Glass", href: "/services/custom-shower-glass" },
  { name: "Window Glass Replacement", href: "/services/window-glass-replacement" },
  { name: "Custom Mirrors", href: "/services/custom-mirrors" },
];

const commercialServices = [
  { name: "Storefront & Commercial Glass", href: "/services/commercial-glass" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (href) => {
    if (href.startsWith("#") && !isHomePage) {
      return "/";
    }
    return href;
  };

  return (
    <>
      <header
        data-testid="main-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "glassmorphism shadow-sm" : "bg-white/80 backdrop-blur-sm"
        }`}
      >
        <div className="section-container">
          <div className="flex items-center justify-between h-20">
            <Link to="/" data-testid="logo-link" className="flex items-center">
              <img 
                src="/logo.jpg" 
                alt="Brunswick Glass" 
                className="h-14 w-auto object-contain"
              />
            </Link>

            <nav className="hidden lg:flex items-center gap-10">
              <Link
                to="/"
                data-testid="nav-home"
                className="font-body text-sm font-medium text-[#1A202C] hover:text-[#0F4C81] transition-colors"
              >
                Home
              </Link>
              
              {/* Services Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <button
                  data-testid="nav-services"
                  className="font-body text-sm font-medium text-[#1A202C] hover:text-[#0F4C81] transition-colors flex items-center gap-1"
                >
                  Services
                  <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
                </button>
                
                <AnimatePresence>
                  {servicesOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full left-0 mt-2 w-72 bg-white shadow-xl border border-gray-100"
                    >
                      <div className="p-4">
                        <div className="mb-4">
                          <span className="font-accent text-xs font-semibold tracking-widest uppercase text-[#0F4C81]">
                            Residential
                          </span>
                          <div className="mt-2 space-y-1">
                            {residentialServices.map((service) => (
                              <Link
                                key={service.href}
                                to={service.href}
                                className="block py-2 px-3 font-body text-sm text-[#4B5563] hover:text-[#0F4C81] hover:bg-[#F9F9F7] transition-colors"
                              >
                                {service.name}
                              </Link>
                            ))}
                          </div>
                        </div>
                        <div className="pt-4 border-t border-gray-100">
                          <span className="font-accent text-xs font-semibold tracking-widest uppercase text-[#0F4C81]">
                            Commercial
                          </span>
                          <div className="mt-2 space-y-1">
                            {commercialServices.map((service) => (
                              <Link
                                key={service.href}
                                to={service.href}
                                className="block py-2 px-3 font-body text-sm text-[#4B5563] hover:text-[#0F4C81] hover:bg-[#F9F9F7] transition-colors"
                              >
                                {service.name}
                              </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {isHomePage ? (
                <a
                  href="#about"
                  data-testid="nav-about"
                  className="font-body text-sm font-medium text-[#1A202C] hover:text-[#0F4C81] transition-colors"
                >
                  About
                </a>
              ) : (
                <Link
                  to="/about"
                  data-testid="nav-about"
                  className="font-body text-sm font-medium text-[#1A202C] hover:text-[#0F4C81] transition-colors"
                >
                  About
                </Link>
              )}
              
              {isHomePage ? (
                <a
                  href="#contact"
                  data-testid="nav-contact"
                  className="font-body text-sm font-medium text-[#1A202C] hover:text-[#0F4C81] transition-colors"
                >
                  Contact
                </a>
              ) : (
                <Link
                  to="/contact"
                  data-testid="nav-contact"
                  className="font-body text-sm font-medium text-[#1A202C] hover:text-[#0F4C81] transition-colors"
                >
                  Contact
                </Link>
              )}
            </nav>

            <div className="hidden lg:flex items-center gap-4">
              <a
                href="tel:910-457-4757"
                data-testid="header-phone"
                className="flex items-center gap-2 font-body text-sm font-semibold text-[#0F4C81]"
              >
                <Phone className="w-4 h-4" />
                910-457-4757
              </a>
              <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
                <DialogTrigger asChild>
                  <Button
                    data-testid="header-quote-btn"
                    className="bg-[#0F4C81] hover:bg-[#0A355C] text-white rounded-full px-6 h-11 font-body font-semibold transition-all duration-300 hover:scale-105"
                  >
                    Get a Quote
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px] p-8">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl text-[#1A202C]">Request a Free Quote</DialogTitle>
                  </DialogHeader>
                  <QuoteForm onClose={() => setQuoteOpen(false)} />
                </DialogContent>
              </Dialog>
            </div>

            <button
              data-testid="mobile-menu-btn"
              className="lg:hidden p-2"
              onClick={() => setMobileMenuOpen(true)}
            >
              <Menu className="w-6 h-6 text-[#1A202C]" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-white z-[100] overflow-y-auto"
          >
            <div className="flex flex-col min-h-full">
              <div className="flex items-center justify-between p-6 border-b">
                <img src="/logo.jpg" alt="Brunswick Glass" className="h-12 w-auto object-contain" />
                <button data-testid="mobile-menu-close" onClick={() => setMobileMenuOpen(false)}>
                  <X className="w-6 h-6 text-[#1A202C]" />
                </button>
              </div>
              
              <nav className="flex flex-col p-6 gap-2">
                <Link
                  to="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className="font-heading text-2xl font-medium text-[#1A202C] py-3 border-b border-gray-100"
                >
                  Home
                </Link>
                
                <div className="py-3 border-b border-gray-100">
                  <span className="font-accent text-xs font-semibold tracking-widest uppercase text-[#0F4C81] mb-3 block">
                    Residential Services
                  </span>
                  {residentialServices.map((service) => (
                    <Link
                      key={service.href}
                      to={service.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="block py-2 font-body text-lg text-[#4B5563]"
                    >
                      {service.name}
                    </Link>
                  ))}
                </div>
                
                <div className="py-3 border-b border-gray-100">
                  <span className="font-accent text-xs font-semibold tracking-widest uppercase text-[#0F4C81] mb-3 block">
                    Commercial Services
                  </span>
                  {commercialServices.map((service) => (
                    <Link
                      key={service.href}
                      to={service.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="block py-2 font-body text-lg text-[#4B5563]"
                    >
                      {service.name}
                    </Link>
                  ))}
                </div>
                
                <Link
                  to="/about"
                  onClick={() => setMobileMenuOpen(false)}
                  className="font-heading text-2xl font-medium text-[#1A202C] py-3 border-b border-gray-100"
                >
                  About
                </Link>
                
                <Link
                  to="/contact"
                  onClick={() => setMobileMenuOpen(false)}
                  className="font-heading text-2xl font-medium text-[#1A202C] py-3 border-b border-gray-100"
                >
                  Contact
                </Link>
              </nav>
              
              <div className="mt-auto p-6 bg-[#F9F9F7]">
                <a
                  href="tel:910-457-4757"
                  className="flex items-center justify-center gap-2 font-body text-xl font-semibold text-[#0F4C81] mb-4"
                >
                  <Phone className="w-5 h-5" />
                  910-457-4757
                </a>
                <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
                  <DialogTrigger asChild>
                    <Button className="w-full bg-[#0F4C81] hover:bg-[#0A355C] text-white rounded-full h-14 font-body font-semibold">
                      Get a Free Quote
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[500px] p-8">
                    <DialogHeader>
                      <DialogTitle className="font-heading text-2xl">Request a Free Quote</DialogTitle>
                    </DialogHeader>
                    <QuoteForm onClose={() => setQuoteOpen(false)} />
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
